import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface BookContentWarning {
  id: string;
  book_id: string;
  user_id: string;
  warning_name: string;
  details: string | null;
  created_at: string;
}

export interface WarningWithCount {
  warning_name: string;
  count: number;
  userHasAdded: boolean;
}

export function useBookContentWarnings(bookId: string | undefined) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['book-content-warnings', bookId],
    queryFn: async () => {
      if (!bookId) return [];
      
      const { data, error } = await supabase
        .from('book_content_warnings')
        .select('*')
        .eq('book_id', bookId);

      if (error) throw error;
      return data as BookContentWarning[];
    },
    enabled: !!bookId,
  });

  // Aggregate warnings with counts
  const warningsWithCounts: WarningWithCount[] = [];
  const warningCounts: Record<string, { count: number; userHasAdded: boolean }> = {};

  if (data) {
    for (const warning of data) {
      if (!warningCounts[warning.warning_name]) {
        warningCounts[warning.warning_name] = { count: 0, userHasAdded: false };
      }
      warningCounts[warning.warning_name].count++;
      if (user && warning.user_id === user.id) {
        warningCounts[warning.warning_name].userHasAdded = true;
      }
    }

    for (const [warning_name, { count, userHasAdded }] of Object.entries(warningCounts)) {
      warningsWithCounts.push({ warning_name, count, userHasAdded });
    }

    // Sort by count descending
    warningsWithCounts.sort((a, b) => b.count - a.count);
  }

  const userWarnings = data?.filter(w => user && w.user_id === user.id) || [];

  const addWarningMutation = useMutation({
    mutationFn: async ({ warningName, details }: { warningName: string; details?: string }) => {
      if (!user || !bookId) throw new Error('Not authenticated or no book');

      const { error } = await supabase
        .from('book_content_warnings')
        .insert({
          book_id: bookId,
          user_id: user.id,
          warning_name: warningName,
          details: details || null,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['book-content-warnings', bookId] });
      toast({ title: 'Content warning added' });
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate')) {
        toast({ title: 'You already added this warning', variant: 'destructive' });
      } else {
        toast({ title: 'Failed to add warning', variant: 'destructive' });
      }
    },
  });

  const removeWarningMutation = useMutation({
    mutationFn: async (warningName: string) => {
      if (!user || !bookId) throw new Error('Not authenticated or no book');

      const { error } = await supabase
        .from('book_content_warnings')
        .delete()
        .eq('book_id', bookId)
        .eq('user_id', user.id)
        .eq('warning_name', warningName);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['book-content-warnings', bookId] });
      toast({ title: 'Content warning removed' });
    },
    onError: () => {
      toast({ title: 'Failed to remove warning', variant: 'destructive' });
    },
  });

  return {
    allWarnings: data || [],
    warningsWithCounts,
    userWarnings,
    totalWarningCount: warningsWithCounts.length,
    isLoading,
    error,
    addWarning: addWarningMutation.mutate,
    removeWarning: removeWarningMutation.mutate,
    isAdding: addWarningMutation.isPending,
    isRemoving: removeWarningMutation.isPending,
  };
}
